import { faro } from '@grafana/faro-react'
import { collectDeviceDetails, toSessionCapabilityFaroAttributes, toSnapshotDeviceFaroAttributes } from 'services/faro/collectDeviceDetails'
import { collectAppDetails } from 'services/zoomDiagnostics/collectAppDetails'
import { isNetworkRelatedConnectionChange } from 'services/zoomDiagnostics/removalCause'
import type { ZoomSDKClient, ZoomSDKStream } from 'zoom/types'

interface NetworkQualityPayload {
    level?: number
    type?: string
    userId?: number
}

interface VideoStatisticData {
    encoding?: boolean
    rtt?: number
    jitter?: number
    bitrate?: number
    fps?: number
    width?: number
    height?: number
    avg_loss?: number
    max_loss?: number
    bandwidth?: number
}

interface VideoStatisticPayload {
    data?: VideoStatisticData
}

interface AudioStatisticData {
    encoding?: boolean
    sample_rate?: number
    avg_loss?: number
    max_loss?: number
    jitter?: number
    rtt?: number
    bandwidth?: number
    bitrate?: number
}

interface AudioStatisticPayload {
    data?: AudioStatisticData
}

interface ConnectionChangePayload {
    state?: string
    reason?: string
}

interface VideoDirectionBuffers {
    rtt: number[]
    jitter: number[]
    bitrate: number[]
    fps: number[]
    bandwidth: number[]
    maxLoss: number[]
}

interface AudioDirectionBuffers {
    rtt: number[]
    jitter: number[]
    bitrate: number[]
    bandwidth: number[]
    maxLoss: number[]
}

interface MetricSampleBuffers {
    videoSend: VideoDirectionBuffers
    videoRecv: VideoDirectionBuffers
    audioSend: AudioDirectionBuffers
    audioRecv: AudioDirectionBuffers
}

interface LatestMetrics {
    uplinkLevel?: number
    downlinkLevel?: number
    videoSendRtt?: number
    videoSendJitter?: number
    videoSendBitrate?: number
    videoSendFps?: number
    videoSendWidth?: number
    videoSendHeight?: number
    videoSendAvgLoss?: number
    videoSendBandwidth?: number
    videoRecvRtt?: number
    videoRecvJitter?: number
    videoRecvBitrate?: number
    videoRecvFps?: number
    videoRecvWidth?: number
    videoRecvHeight?: number
    videoRecvAvgLoss?: number
    videoRecvBandwidth?: number
    audioSendRtt?: number
    audioSendJitter?: number
    audioSendAvgLoss?: number
    audioSendSampleRate?: number
    audioSendBandwidth?: number
    audioSendBitrate?: number
    audioRecvRtt?: number
    audioRecvJitter?: number
    audioRecvAvgLoss?: number
    audioRecvSampleRate?: number
    audioRecvBandwidth?: number
    audioRecvBitrate?: number
    connectionState?: string
    connectionReason?: string
}

export type SnapshotTrigger =
    | 'session-start'
    | 'mid-session'
    | 'connection-change'
    | 'poor-network'
    | 'network-disconnect'
    | 'session-end'

export interface StartZoomQualityMonitorOptions {
    midSessionDelayMs?: number
}

const MEASUREMENT_KEYS = [
    'videoRecvAvgLoss',
    'videoRecvLossMax',
    'videoRecvRttAvg',
    'videoRecvRttMax',
    'videoRecvJitterAvg',
    'videoRecvJitterMax',
    'videoRecvBandwidth',
    'audioRecvAvgLoss',
    'audioRecvLossMax',
    'audioRecvRttAvg',
    'audioRecvRttMax',
    'audioRecvJitterAvg',
    'audioRecvJitterMax',
    'audioRecvBandwidth'
] as const

let midSessionTimeoutId: ReturnType<typeof setTimeout> | null = null
let networkDisconnectSnapshotSent = false
let latestMetrics: LatestMetrics = {}
let sampleBuffers: MetricSampleBuffers = createEmptySampleBuffers()
let flushedAggregates: Record<string, number> = {}
let activeClient: ZoomSDKClient | null = null
let activeStream: ZoomSDKStream | null = null

const eventHandlers = new Map<string, (payload: unknown) => void>()

function createEmptyVideoDirectionBuffers (): VideoDirectionBuffers {
    return {
        rtt: [],
        jitter: [],
        bitrate: [],
        fps: [],
        bandwidth: [],
        maxLoss: []
    }
}

function createEmptyAudioDirectionBuffers (): AudioDirectionBuffers {
    return {
        rtt: [],
        jitter: [],
        bitrate: [],
        bandwidth: [],
        maxLoss: []
    }
}

function createEmptySampleBuffers (): MetricSampleBuffers {
    return {
        videoSend: createEmptyVideoDirectionBuffers(),
        videoRecv: createEmptyVideoDirectionBuffers(),
        audioSend: createEmptyAudioDirectionBuffers(),
        audioRecv: createEmptyAudioDirectionBuffers()
    }
}

const pushSample = (samples: number[], value: number | undefined): void => {
    if (value === undefined || Number.isNaN(value)) {
        return
    }

    samples.push(value)
}

const computeAvgMax = (samples: number[]): { avg?: number; max?: number } => {
    if (samples.length === 0) {
        return {}
    }

    const sum = samples.reduce((total, value) => total + value, 0)
    return {
        avg: sum / samples.length,
        max: Math.max(...samples)
    }
}

const appendAggregatedMetrics = (
    attributes: Record<string, string>,
    prefix: string,
    metricName: string,
    samples: number[]
): void => {
    const { avg, max } = computeAvgMax(samples)

    if (avg !== undefined) {
        attributes[`${prefix}${metricName}Avg`] = String(avg)
    }

    if (max !== undefined) {
        attributes[`${prefix}${metricName}Max`] = String(max)
    }
}

const flushSampleBuffers = (): Record<string, number> => {
    const aggregates: Record<string, number> = {}
    const attributes: Record<string, string> = {}

    appendAggregatedMetrics(attributes, 'videoSend', 'Rtt', sampleBuffers.videoSend.rtt)
    appendAggregatedMetrics(attributes, 'videoSend', 'Jitter', sampleBuffers.videoSend.jitter)
    appendAggregatedMetrics(attributes, 'videoSend', 'Bitrate', sampleBuffers.videoSend.bitrate)
    appendAggregatedMetrics(attributes, 'videoSend', 'Fps', sampleBuffers.videoSend.fps)
    appendAggregatedMetrics(attributes, 'videoSend', 'Bandwidth', sampleBuffers.videoSend.bandwidth)
    appendAggregatedMetrics(attributes, 'videoSend', 'Loss', sampleBuffers.videoSend.maxLoss)

    appendAggregatedMetrics(attributes, 'videoRecv', 'Rtt', sampleBuffers.videoRecv.rtt)
    appendAggregatedMetrics(attributes, 'videoRecv', 'Jitter', sampleBuffers.videoRecv.jitter)
    appendAggregatedMetrics(attributes, 'videoRecv', 'Bitrate', sampleBuffers.videoRecv.bitrate)
    appendAggregatedMetrics(attributes, 'videoRecv', 'Fps', sampleBuffers.videoRecv.fps)
    appendAggregatedMetrics(attributes, 'videoRecv', 'Bandwidth', sampleBuffers.videoRecv.bandwidth)
    appendAggregatedMetrics(attributes, 'videoRecv', 'Loss', sampleBuffers.videoRecv.maxLoss)

    appendAggregatedMetrics(attributes, 'audioSend', 'Rtt', sampleBuffers.audioSend.rtt)
    appendAggregatedMetrics(attributes, 'audioSend', 'Jitter', sampleBuffers.audioSend.jitter)
    appendAggregatedMetrics(attributes, 'audioSend', 'Bitrate', sampleBuffers.audioSend.bitrate)
    appendAggregatedMetrics(attributes, 'audioSend', 'Bandwidth', sampleBuffers.audioSend.bandwidth)
    appendAggregatedMetrics(attributes, 'audioSend', 'Loss', sampleBuffers.audioSend.maxLoss)

    appendAggregatedMetrics(attributes, 'audioRecv', 'Rtt', sampleBuffers.audioRecv.rtt)
    appendAggregatedMetrics(attributes, 'audioRecv', 'Jitter', sampleBuffers.audioRecv.jitter)
    appendAggregatedMetrics(attributes, 'audioRecv', 'Bitrate', sampleBuffers.audioRecv.bitrate)
    appendAggregatedMetrics(attributes, 'audioRecv', 'Bandwidth', sampleBuffers.audioRecv.bandwidth)
    appendAggregatedMetrics(attributes, 'audioRecv', 'Loss', sampleBuffers.audioRecv.maxLoss)

    for (const [key, value] of Object.entries(attributes)) {
        aggregates[key] = parseFloat(value)
    }

    sampleBuffers = createEmptySampleBuffers()
    flushedAggregates = aggregates
    return aggregates
}

const appendOptionalString = (
    attributes: Record<string, string>,
    key: string,
    value: number | string | undefined
): void => {
    if (value !== undefined) {
        attributes[key] = String(value)
    }
}

const appendLatestMetrics = (attributes: Record<string, string>): void => {
    appendOptionalString(attributes, 'uplinkLevel', latestMetrics.uplinkLevel)
    appendOptionalString(attributes, 'downlinkLevel', latestMetrics.downlinkLevel)

    appendOptionalString(attributes, 'videoSendRtt', latestMetrics.videoSendRtt)
    appendOptionalString(attributes, 'videoSendJitter', latestMetrics.videoSendJitter)
    appendOptionalString(attributes, 'videoSendBitrate', latestMetrics.videoSendBitrate)
    appendOptionalString(attributes, 'videoSendFps', latestMetrics.videoSendFps)
    appendOptionalString(attributes, 'videoSendWidth', latestMetrics.videoSendWidth)
    appendOptionalString(attributes, 'videoSendHeight', latestMetrics.videoSendHeight)
    appendOptionalString(attributes, 'videoSendAvgLoss', latestMetrics.videoSendAvgLoss)
    appendOptionalString(attributes, 'videoSendBandwidth', latestMetrics.videoSendBandwidth)

    appendOptionalString(attributes, 'videoRecvRtt', latestMetrics.videoRecvRtt)
    appendOptionalString(attributes, 'videoRecvJitter', latestMetrics.videoRecvJitter)
    appendOptionalString(attributes, 'videoRecvBitrate', latestMetrics.videoRecvBitrate)
    appendOptionalString(attributes, 'videoRecvFps', latestMetrics.videoRecvFps)
    appendOptionalString(attributes, 'videoRecvWidth', latestMetrics.videoRecvWidth)
    appendOptionalString(attributes, 'videoRecvHeight', latestMetrics.videoRecvHeight)
    appendOptionalString(attributes, 'videoRecvAvgLoss', latestMetrics.videoRecvAvgLoss)
    appendOptionalString(attributes, 'videoRecvBandwidth', latestMetrics.videoRecvBandwidth)

    appendOptionalString(attributes, 'audioSendRtt', latestMetrics.audioSendRtt)
    appendOptionalString(attributes, 'audioSendJitter', latestMetrics.audioSendJitter)
    appendOptionalString(attributes, 'audioSendAvgLoss', latestMetrics.audioSendAvgLoss)
    appendOptionalString(attributes, 'audioSendSampleRate', latestMetrics.audioSendSampleRate)
    appendOptionalString(attributes, 'audioSendBandwidth', latestMetrics.audioSendBandwidth)
    appendOptionalString(attributes, 'audioSendBitrate', latestMetrics.audioSendBitrate)

    appendOptionalString(attributes, 'audioRecvRtt', latestMetrics.audioRecvRtt)
    appendOptionalString(attributes, 'audioRecvJitter', latestMetrics.audioRecvJitter)
    appendOptionalString(attributes, 'audioRecvAvgLoss', latestMetrics.audioRecvAvgLoss)
    appendOptionalString(attributes, 'audioRecvSampleRate', latestMetrics.audioRecvSampleRate)
    appendOptionalString(attributes, 'audioRecvBandwidth', latestMetrics.audioRecvBandwidth)
    appendOptionalString(attributes, 'audioRecvBitrate', latestMetrics.audioRecvBitrate)

    if (latestMetrics.connectionState) {
        attributes.connectionState = latestMetrics.connectionState
    }

    if (latestMetrics.connectionReason) {
        attributes.connectionReason = latestMetrics.connectionReason
    }
}

const subscribeToStatisticData = async (stream: ZoomSDKStream): Promise<void> => {
    await Promise.resolve(stream.subscribeVideoStatisticData())
    await Promise.resolve(stream.subscribeAudioStatisticData())
}

const flattenSnapshot = (trigger: SnapshotTrigger): Record<string, string> => {
    const device = collectDeviceDetails({
        getMaxRenderableVideos: activeStream
            ? () => activeStream?.getMaxRenderableVideos() ?? 0
            : undefined
    })
    const app = collectAppDetails({
        sessionInfo: activeClient?.getSessionInfo?.()
    })

    const attributes: Record<string, string> = {
        ...toSnapshotDeviceFaroAttributes(device),
        ...(trigger === 'session-start' ? toSessionCapabilityFaroAttributes(device) : {}),
        classId: app.classroomId,
        wseUserId: app.wseUserId,
        role: app.role,
        gitSha: app.gitSha,
        zoomSdkVersion: app.zoomSdkVersion,
        appName: app.appName,
        environment: app.environment,
        trigger
    }

    if (app.zoomTopic) attributes.zoomTopic = app.zoomTopic
    if (app.zoomUserId) attributes.zoomUserId = app.zoomUserId
    if (app.zoomUserName) attributes.zoomUserName = app.zoomUserName

    appendLatestMetrics(attributes)

    for (const [key, value] of Object.entries(flushedAggregates)) {
        attributes[key] = String(value)
    }

    return attributes
}

const buildMeasurementValues = (): Record<string, number> => {
    const values: Record<string, number> = {}

    for (const key of MEASUREMENT_KEYS) {
        if (flushedAggregates[key] !== undefined) {
            values[key] = flushedAggregates[key]
            continue
        }

        if (key === 'videoRecvAvgLoss' && latestMetrics.videoRecvAvgLoss !== undefined) {
            values[key] = latestMetrics.videoRecvAvgLoss
        } else if (key === 'audioRecvAvgLoss' && latestMetrics.audioRecvAvgLoss !== undefined) {
            values[key] = latestMetrics.audioRecvAvgLoss
        } else if (key === 'videoRecvBandwidth' && latestMetrics.videoRecvBandwidth !== undefined) {
            values[key] = latestMetrics.videoRecvBandwidth
        } else if (key === 'audioRecvBandwidth' && latestMetrics.audioRecvBandwidth !== undefined) {
            values[key] = latestMetrics.audioRecvBandwidth
        }
    }

    return values
}

const pushQualitySnapshot = (trigger: SnapshotTrigger): void => {
    if (!faro.api) {
        return
    }

    flushSampleBuffers()
    const attributes = flattenSnapshot(trigger)
    faro.api.pushEvent('zoom_quality_snapshot', attributes)

    const values = buildMeasurementValues()
    if (Object.keys(values).length > 0) {
        faro.api.pushMeasurement?.({ type: 'zoom_quality', values })
    }
}

const shouldPushImmediately = (): boolean => {
    const uplink = latestMetrics.uplinkLevel
    const downlink = latestMetrics.downlinkLevel
    return (uplink !== undefined && uplink <= 1) || (downlink !== undefined && downlink <= 1)
}

const registerHandler = (event: string, handler: (payload: unknown) => void): void => {
    eventHandlers.set(event, handler)
    activeClient?.on(event, handler)
}

const handleVideoStatisticData = (data: VideoStatisticData): void => {
    const isSend = data.encoding === true
    const buffers = isSend ? sampleBuffers.videoSend : sampleBuffers.videoRecv

    pushSample(buffers.rtt, data.rtt)
    pushSample(buffers.jitter, data.jitter)
    pushSample(buffers.bitrate, data.bitrate)
    pushSample(buffers.fps, data.fps)
    pushSample(buffers.bandwidth, data.bandwidth)
    pushSample(buffers.maxLoss, data.max_loss)

    if (isSend) {
        if (data.rtt !== undefined) latestMetrics.videoSendRtt = data.rtt
        if (data.jitter !== undefined) latestMetrics.videoSendJitter = data.jitter
        if (data.bitrate !== undefined) latestMetrics.videoSendBitrate = data.bitrate
        if (data.fps !== undefined) latestMetrics.videoSendFps = data.fps
        if (data.width !== undefined) latestMetrics.videoSendWidth = data.width
        if (data.height !== undefined) latestMetrics.videoSendHeight = data.height
        if (data.avg_loss !== undefined) latestMetrics.videoSendAvgLoss = data.avg_loss
        if (data.bandwidth !== undefined) latestMetrics.videoSendBandwidth = data.bandwidth
    } else {
        if (data.rtt !== undefined) latestMetrics.videoRecvRtt = data.rtt
        if (data.jitter !== undefined) latestMetrics.videoRecvJitter = data.jitter
        if (data.bitrate !== undefined) latestMetrics.videoRecvBitrate = data.bitrate
        if (data.fps !== undefined) latestMetrics.videoRecvFps = data.fps
        if (data.width !== undefined) latestMetrics.videoRecvWidth = data.width
        if (data.height !== undefined) latestMetrics.videoRecvHeight = data.height
        if (data.avg_loss !== undefined) latestMetrics.videoRecvAvgLoss = data.avg_loss
        if (data.bandwidth !== undefined) latestMetrics.videoRecvBandwidth = data.bandwidth
    }
}

const handleAudioStatisticData = (data: AudioStatisticData): void => {
    const isSend = data.encoding === true
    const buffers = isSend ? sampleBuffers.audioSend : sampleBuffers.audioRecv

    pushSample(buffers.rtt, data.rtt)
    pushSample(buffers.jitter, data.jitter)
    pushSample(buffers.bitrate, data.bitrate)
    pushSample(buffers.bandwidth, data.bandwidth)
    pushSample(buffers.maxLoss, data.max_loss)

    if (isSend) {
        if (data.rtt !== undefined) latestMetrics.audioSendRtt = data.rtt
        if (data.jitter !== undefined) latestMetrics.audioSendJitter = data.jitter
        if (data.avg_loss !== undefined) latestMetrics.audioSendAvgLoss = data.avg_loss
        if (data.sample_rate !== undefined) latestMetrics.audioSendSampleRate = data.sample_rate
        if (data.bandwidth !== undefined) latestMetrics.audioSendBandwidth = data.bandwidth
        if (data.bitrate !== undefined) latestMetrics.audioSendBitrate = data.bitrate
    } else {
        if (data.rtt !== undefined) latestMetrics.audioRecvRtt = data.rtt
        if (data.jitter !== undefined) latestMetrics.audioRecvJitter = data.jitter
        if (data.avg_loss !== undefined) latestMetrics.audioRecvAvgLoss = data.avg_loss
        if (data.sample_rate !== undefined) latestMetrics.audioRecvSampleRate = data.sample_rate
        if (data.bandwidth !== undefined) latestMetrics.audioRecvBandwidth = data.bandwidth
        if (data.bitrate !== undefined) latestMetrics.audioRecvBitrate = data.bitrate
    }
}

export const startZoomQualityMonitor = async (
    client: ZoomSDKClient,
    stream: ZoomSDKStream,
    options: StartZoomQualityMonitorOptions = {}
): Promise<void> => {
    if (!faro.api) {
        return
    }

    stopZoomQualityMonitor()

    activeClient = client
    activeStream = stream
    latestMetrics = {}
    sampleBuffers = createEmptySampleBuffers()
    flushedAggregates = {}

    try {
        await subscribeToStatisticData(stream)
    } catch (error) {
        console.warn('[zoomQualityMonitor] Failed to subscribe to statistic data:', error)
    }

    registerHandler('network-quality-change', payload => {
        const data = payload as NetworkQualityPayload
        if (data.type === 'uplink') {
            latestMetrics.uplinkLevel = data.level
        } else if (data.type === 'downlink') {
            latestMetrics.downlinkLevel = data.level
        }

        if (shouldPushImmediately()) {
            pushQualitySnapshot('poor-network')
        }
    })

    registerHandler('video-statistic-data-change', payload => {
        const data = (payload as VideoStatisticPayload).data
        if (!data) return

        handleVideoStatisticData(data)
    })

    registerHandler('audio-statistic-data-change', payload => {
        const data = (payload as AudioStatisticPayload).data
        if (!data) return

        handleAudioStatisticData(data)
    })

    registerHandler('connection-change', payload => {
        const data = payload as ConnectionChangePayload
        latestMetrics.connectionState = data.state
        latestMetrics.connectionReason = data.reason

        if (isNetworkRelatedConnectionChange(data.state, data.reason)) {
            pushQualitySnapshot('network-disconnect')
            networkDisconnectSnapshotSent = true
            return
        }

        if (data.state !== 'Closed' && data.state !== 'Fail') {
            pushQualitySnapshot('connection-change')
        }
    })

    pushQualitySnapshot('session-start')

    const midSessionDelayMs = options.midSessionDelayMs
    if (midSessionDelayMs !== undefined && midSessionDelayMs > 0) {
        midSessionTimeoutId = setTimeout(() => {
            pushQualitySnapshot('mid-session')
        }, midSessionDelayMs)
    }
}

export const stopZoomQualityMonitor = (): void => {
    if (midSessionTimeoutId !== null) {
        clearTimeout(midSessionTimeoutId)
        midSessionTimeoutId = null
    }

    if (activeClient && !networkDisconnectSnapshotSent) {
        pushQualitySnapshot('session-end')
    }

    networkDisconnectSnapshotSent = false

    if (activeClient) {
        for (const [event, handler] of eventHandlers.entries()) {
            activeClient.off(event, handler)
        }
    }

    eventHandlers.clear()
    activeClient = null
    activeStream = null
    latestMetrics = {}
    sampleBuffers = createEmptySampleBuffers()
    flushedAggregates = {}
}
